import { useMemo, type ReactNode } from 'react'
import clsx from 'clsx'

import { ScrollArea } from '@/components/shadcn/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { getComponentHeights } from '../utils'
import { resolveFinancialStatementView, type FinancialStatementView } from './financialStatementModel'
import { FinancialStatementTable } from './FinancialStatementTable'
import { FinancialStatementDataSchema, FinancialStatementUISchema } from './types'

export function FinancialStatementContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: FinancialStatementUISchema
  dataSchema: FinancialStatementDataSchema
  tileFilters?: ReactNode
}) {
  const { title, description, emphasizeHeader, statements: statementUiSchemas = [] } = uiSchema
  const hasHeader = !!title || !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  const views = useMemo(
    () => resolveFinancialStatementView(uiSchema, dataSchema),
    [uiSchema, dataSchema]
  )

  const renderInTabs =
    views.length > 1 && statementUiSchemas.some((statement) => statement.renderInTabs === true)

  const renderStatement = (view: FinancialStatementView, { hideTitle = false }: { hideTitle?: boolean } = {}) => (
    <FinancialStatementTable key={view.id} view={view} hideTitle={hideTitle} />
  )

  if (views.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {hasHeader && (
          <ComponentHeader emphasize={emphasizeHeader}>
            <ComponentTitle title={title} description={description} />
            {tileFilters && <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>}
          </ComponentHeader>
        )}
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          No statements configured
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          {tileFilters && <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>}
        </ComponentHeader>
      )}
      <ScrollArea className={clsx({ 'pt-5': hasHeader })} style={{ height: contentHeight }}>
        <div className="space-y-8 px-4 pb-6">
          {renderInTabs ? (
            <Tabs defaultValue={views[0]?.id} className="w-full">
              <TabsList className="h-auto w-full flex-wrap justify-start">
                {views.map((view) => (
                  <TabsTrigger
                    key={view.id}
                    value={view.id}
                    className="group [&[data-state=active]]:bg-background"
                  >
                    <span className="grid text-center">
                      <span aria-hidden className="invisible col-start-1 row-start-1 font-bold">
                        {view.title}
                      </span>
                      <span className="col-start-1 row-start-1 group-data-[state=active]:font-bold">
                        {view.title}
                      </span>
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {views.map((view) => (
                <TabsContent key={view.id} value={view.id} className="mt-4">
                  {renderStatement(view, { hideTitle: true })}
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            views.map((view) => renderStatement(view))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
